import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import { User } from "../api";
import LinkTab from "./link-tab";

interface CommonProps {
	users: User[];
	groupParam: number | null;
	dataComponentPrefix: string;
}

interface UserLevelProps extends CommonProps {
	/** Group tabs plus one row of user tabs; the choice is a user. */
	level?: "user";
	selectedUserId: number | null;
	onSelectUser: (user: User) => void;
}

interface GroupLevelProps extends CommonProps {
	/** Group tabs only, for views that belong to a whole group; the choice is a group. */
	level: "group";
	onSelectGroup: (groupId: number) => void;
}

type UserGroupTabSelectorProps = UserLevelProps | GroupLevelProps;

interface UserGroup {
	id: number;
	name: string;
	users: User[];
}

/**
 * Group + user tab switcher shared by the Todo, Boards and Candidate pool views.
 * Renders one row of group tabs (when more than one group exists) and, at the
 * default `level="user"`, one row of user tabs for the active group. Owns no
 * URL/selection state — the parent passes the current selection in and
 * receives the chosen user (or, at `level="group"`, the chosen group id).
 * Returns null when there is nothing meaningful to switch between.
 */
export const UserGroupTabSelector = (props: UserGroupTabSelectorProps) => {
	const { users, groupParam, dataComponentPrefix } = props;
	const usersWithProfile = users.filter((u) => u.userprofile?.up_group);
	if (usersWithProfile.length <= 1) return null;

	const groupsById = new Map<number, UserGroup>();
	usersWithProfile.forEach((u) => {
		const g = u.userprofile.up_group;
		if (!groupsById.has(g.id)) {
			groupsById.set(g.id, { id: g.id, name: g.name, users: [] });
		}
		groupsById.get(g.id).users.push(u);
	});
	const groups = Array.from(groupsById.values()).sort((a, b) =>
		a.name.localeCompare(b.name)
	);
	const showGroups = groups.length > 1;

	if (props.level === "group") {
		if (!showGroups) return null;
		const activeGroup = groups.find((g) => g.id === groupParam) ?? groups[0];
		return (
			<Box pt={3} px={3} data-component={`${dataComponentPrefix}-group-selector`}>
				<Tabs
					value={activeGroup.id}
					onChange={(_, val: number) => props.onSelectGroup(val)}
					variant="scrollable"
					scrollButtons="auto"
					data-component={`${dataComponentPrefix}-group-tabs`}
				>
					{groups.map((group) => (
						<LinkTab
							key={`group-tab-${group.id}`}
							label={group.name}
							value={group.id}
							href={`?group=${group.id}`}
							data-component={`${dataComponentPrefix}-group-tab`}
						/>
					))}
				</Tabs>
			</Box>
		);
	}

	const { selectedUserId, onSelectUser } = props;
	const selectedUser = users.find((u) => u.id === selectedUserId);
	const activeGroupId =
		selectedUser?.userprofile?.up_group?.id ?? groupParam ?? groups[0].id;
	const activeGroup = groups.find((g) => g.id === activeGroupId) ?? groups[0];
	const groupUsers = activeGroup.users;

	const switchGroup = (groupId: number) => {
		const group = groups.find((g) => g.id === groupId);
		if (!group) return;
		const stillValid = group.users.find((u) => u.id === selectedUserId);
		onSelectUser(stillValid ?? group.users[0]);
	};

	const showUsers = showGroups || groupUsers.length > 1;
	if (!showGroups && !showUsers) return null;

	return (
		<Box pt={3} px={3} data-component={`${dataComponentPrefix}-user-selector`}>
			{showGroups && (
				<Tabs
					value={activeGroup.id}
					onChange={(_, val: number) => switchGroup(val)}
					variant="scrollable"
					scrollButtons="auto"
					data-component={`${dataComponentPrefix}-group-tabs`}
				>
					{groups.map((group) => (
						<LinkTab
							key={`group-tab-${group.id}`}
							label={group.name}
							value={group.id}
							href={`?group=${group.id}&user=${
								(
									group.users.find((u) => u.id === selectedUserId) ??
									group.users[0]
								).id
							}`}
							data-component={`${dataComponentPrefix}-group-tab`}
						/>
					))}
				</Tabs>
			)}
			{showUsers && (
				<Tabs
					value={
						groupUsers.find((u) => u.id === selectedUserId)?.id ??
						groupUsers[0].id
					}
					onChange={(_, val: number) => {
						const next = groupUsers.find((u) => u.id === val);
						if (next) onSelectUser(next);
					}}
					variant="scrollable"
					scrollButtons="auto"
					data-component={`${dataComponentPrefix}-user-tabs`}
				>
					{groupUsers.map((user) => (
						<LinkTab
							key={`user-tab-${user.id}`}
							label={user.first_name}
							value={user.id}
							href={`?group=${activeGroup.id}&user=${user.id}`}
							data-component={`${dataComponentPrefix}-user-tab`}
						/>
					))}
				</Tabs>
			)}
		</Box>
	);
};
